import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Search } from "lucide-react";

interface TypingSearchBarProps {
  /** Typing loops only while the slide is active; resets when it isn't. */
  active: boolean;
  query: string;
  className?: string;
}

/**
 * Glassy search bar that types its query on loop with a blinking cursor —
 * the same motif as the deck's title slide, reimplemented chapter-locally.
 */
export function TypingSearchBar({ active, query, className = "" }: TypingSearchBarProps) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!active) {
      setTyped("");
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = (fn: () => void, ms: number) => {
      timer = setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
    };
    const typeTo = (i: number) => {
      setTyped(query.slice(0, i));
      if (i < query.length) schedule(() => typeTo(i + 1), 70 + Math.random() * 55);
      else schedule(() => eraseTo(query.length - 1), 2400);
    };
    const eraseTo = (i: number) => {
      setTyped(query.slice(0, i));
      if (i > 0) schedule(() => eraseTo(i - 1), 30);
      else schedule(() => typeTo(1), 800);
    };
    schedule(() => typeTo(1), 500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, query]);

  return (
    <div
      className={`flex items-center gap-5 rounded-full border border-white/10 bg-white/5 px-8 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-xl ${className}`}
    >
      <Search className="h-7 w-7 shrink-0 text-white/50" aria-hidden />
      <span className="min-w-0 flex-1 whitespace-pre text-left text-2xl text-white/90">
        {typed}
        <motion.span
          className="ml-1 inline-block h-6 w-[3px] rounded-full bg-white/80 align-middle"
          animate={{ opacity: [1, 1, 0, 0] }}
          transition={{ duration: 1, repeat: Infinity, times: [0, 0.5, 0.5, 1] }}
        />
      </span>
    </div>
  );
}
